/**
 * @author: lv.chen
 * @date: 2025/2/18
 * @desc: Pipeline/Change Log页面Change Detail页签
 */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { useBoolean } from 'ahooks';
import { Justify, Card } from 'tea-component';
import MultiTypeDownloadBtn from '@intelligence/pages/TopCharts/components/MultiTypeDownloadBtn';
import ToggleAllItems from '@gp/components/ToggleAllItems';
import SearchCommon from '@gp/components/SearchCommon';
import ChangeDetailFilter from './components/ChangeDetailFilter';
import ChangeDetailTable from './components/ChangeDetailTable';
import useRequest from '@gp/hooks/useRequest';
import usePageLog from '@gp/hooks/usePageLog';
import useDomainPageStore from './_hooks/useDomainPageStore';
import usePageFilterInit from '@gp/hooks/usePageFilterInit';
import useDownload, { createDownloadService } from '@gp/hooks/useDownload';
import { KEYS_MAPPING, NAMES_MAPPING } from '@gp/constants/field';
import {
  fetchChangeDetailFilterConditions,
  fetchChangeDetails,
  downloadChangeDetails,
} from '@gp/services/changeLog';
import { getSearchList } from './services';
import {
  toProjectDetails,
  formatSearchProjectsResults,
} from '@gp/utils/project';
import { formatSearchParams } from '@gp/pages/Pipeline/utils';
import {
  formatFilterConditions,
  formatFilterFetchParams,
  formatSortFetchParams,
} from './utils';
import { DEFAULT_FILTER, DEFAULT_SORT } from './models/locker';
import {
  FILE_TYPES_MAP,
  SIMPLE_DOWNLOAD_FILE_TYPES1,
  DEFAULT_SEARCH,
} from '@gp/constants/const';
import { fetchCommonConditions } from '@gp/services/filter';
import type { SortBy } from 'tea-component/lib/table/addons';
import type { IBaseProps } from '@home/types/common';
import type {
  IRecord,
  ISearchPayload,
  ISearchPayloadPageStore,
} from '@gp/types/global';
import type {
  IPipelineChangeDetailFilterConditionsVO,
  IPipelineChangeDetailFilterVO,
  IPipelineChangeDetailVO,
  IPipelineChangeDetailTableRecord,
} from '@gp/types/changeLog';
import { deleteFilterAllValue } from '@gp/utils/utils';
import './index.scss';

type IChangeDetailProps = IBaseProps;

const ChangeDetail: React.FC<IChangeDetailProps> = (props) => {
  const { style, className, headerTitle, scrollBody } = props;
  const currentPage = 'pipelineChangeLogChangeDetail';
  const showRightBtn = true;
  const filterHeightRef = useRef(null);

  const commonCondParams = {
    change_log_time_range: [],
    pteam_bo_studio_project: { project_auth: true },
    game_detail_tab: [],
    change_log_update_user: [],
    stage: [],
  };
  const [selectedValue, setSelectedValue] = useState<any>({});
  // const { loading: filterConditionsLoading2, data: filterConditionsRes2 } = useRequest(
  //   fetchChangeDetailFilterConditions,
  // );
  const { loading: filterConditionsLoading, data: filterConditionsRes } =
    useRequest(fetchCommonConditions, {
      defaultParams: [commonCondParams],
    });

  const {
    values,
    conditions: filterConditions,
    defaultValues: allValuesInterface,
  } = formatFilterConditions(filterConditionsRes);

  const {
    lock,
    filter: lockerFilter,
    sort: lockerSort,
    search: lockerSearch,
  } = useDomainPageStore({
    getFilter: () => filter,
    getSort: () => sort,
    getSearch: () => search,
  });

  const fields = [
    {
      key: 'rangePicker',
      type: 'rangePicker',
      label: '',
      saveKey: 'changeLog_rangePicker',
    },
    {
      key: KEYS_MAPPING.CASCADE_PROJECT,
      type: 'cascadeProject',
      label: NAMES_MAPPING.CASCADE_PROJECT,
    },
    { key: 'tab', type: 'selectMultiple', label: 'Tab' },
    { key: 'update_user', type: 'selectMultiple', label: 'Last Update' },
    { key: 'stage', type: 'selectMultiple', label: 'Stage' },
  ];

  const {
    loading: initialLoading,
    filter: initialFilter,
    onUpdateShortUrlParams,
  } = usePageFilterInit({
    loading: filterConditionsLoading,
    conditions: {
      filter: filterConditions,
    },
    storeValues: {
      lock,
      filter: lockerFilter,
    },
    defaultValues: {
      filter: {
        ...DEFAULT_FILTER,
        rangePicker: {
          ...DEFAULT_FILTER.rangePicker,
          ...values?.rangePicker,
        },
        project_id: values?.project_id || [],
      },
    },
    allFilterOptionsValues: allValuesInterface,
    setSelectedValue,
    fields,
  });

  const { isDownloading, handleDownload } = useDownload({
    csvDownloadLogKey: 'PIPELINE_CHANGE_LOG_CHANGE_DETAIL_DOWNLOAD_CSV_SUBMIT',
    excelDownloadLogKey:
      'PIPELINE_CHANGE_LOG_CHANGE_DETAIL_DOWNLOAD_EXCEL_SUBMIT',
    downloadService: createDownloadService(downloadChangeDetails),
  });

  const [isShowAll, { toggle: toggleIsShowAll }] = useBoolean(false);
  const [expandItemList, setExpandItemList] = useState<any>([]);
  const [tableHeight, setTableHeight] = useState(544);
  const [filter, setFilter] = useState<IPipelineChangeDetailFilterVO>(
    initialFilter as any,
  );
  const [sort, setSort] = useState<SortBy<IPipelineChangeDetailTableRecord>[]>(
    lock ? lockerSort : DEFAULT_SORT,
  );

  const [search, setSearch] = useState<ISearchPayloadPageStore>(
    lock ? lockerSearch : DEFAULT_SEARCH,
  );

  const [changeDetailsLoading, setChangeDetailsLoading] = useState(true);
  const [changeDetailsError, setChangeDetailsError] = useState<
  Error | undefined
  >(undefined);
  const [changeDetails, setChangeDetails] = useState<IPipelineChangeDetailVO[]>(
    [],
  );

  const updateChangeDetails = async (
    filterPayload: IPipelineChangeDetailFilterVO = filter,
    filterConditionsPayload: IPipelineChangeDetailFilterConditionsVO = filterConditions,
    sortPayload: SortBy<IPipelineChangeDetailTableRecord>[] = sort,
    searchPayload: ISearchPayloadPageStore = search,
  ) => {
    try {
      const filterTmp = { ...filter, ...filterPayload };
      const filterParams = formatFilterFetchParams(
        filterTmp,
        filterConditionsPayload,
      );
      const sortParams = formatSortFetchParams(sortPayload);
      const searchParams = formatSearchParams(searchPayload);
      const params = {
        ...filterParams,
        ...sortParams,
        ...searchParams,
      };
      setFilter(filterTmp);
      setChangeDetailsLoading(true);
      const res = await fetchChangeDetails(params);
      const changeDetailsTemp = res?.data?.list || [];
      setChangeDetails(changeDetailsTemp);

      if (isShowAll === true) {
        const projectIdList =
          changeDetailsTemp?.map((item: any) => item?.project_id || '') || [];
        setExpandItemList(projectIdList);
      } else {
        setExpandItemList([]);
      }

      setChangeDetailsError(undefined);
    } catch (exception: any) {
      setChangeDetails([]);
      setChangeDetailsError(exception);
    } finally {
      setChangeDetailsLoading(false);
    }
  };

  const handleFilterChange = (payload?: IPipelineChangeDetailFilterVO) => {
    const filterTmp = { ...filter, ...payload };
    setFilter(filterTmp);
    updateChangeDetails(filterTmp);
    onUpdateShortUrlParams({ filter: filterTmp });
  };

  const handleSortChange = (
    payload?: SortBy<IPipelineChangeDetailTableRecord>[],
  ) => {
    const payloadTmp = payload || [];
    setSort(payloadTmp);
    updateChangeDetails(filter, filterConditions, payloadTmp);
  };

  const handleSearchSubmit = (
    payload: ISearchPayload,
    searchList: IRecord<any>[],
  ) => {
    const {
      search_type: searchType,
      search_keyword: searchKeyword,
      search,
      search_ids: searchIds,
    } = payload || {};
    const searchTmp = {
      search_type: searchType || '',
      search_keyword: searchKeyword || '',
      search: search || [],
      search_ids: searchIds || [],
      search_list: searchList || [],
    };

    setSearch(searchTmp);
    updateChangeDetails(filter, filterConditions, sort, searchTmp);
  };

  const setSearchListHandle = (list: any) => {
    const searchDataTmp = {
      ...search,
      search_list: list || [],
    };
    setSearch(searchDataTmp);
  };
  const setSearchValHandle = (val: any) => {
    const searchDataTmp = {
      ...search,
      search_keyword: val || '',
    };
    setSearch(searchDataTmp);
  };

  const handleRetry = () => {
    updateChangeDetails();
  };

  const handleReset = () => {
    const searchTmp = lock ? lockerSearch : DEFAULT_SEARCH;
    setSearch(searchTmp);
    updateChangeDetails(values, filterConditions, sort, searchTmp);
    onUpdateShortUrlParams({ filter: values });
    setSelectedValue({});
    deleteFilterAllValue();
  };

  const handleShowAll = () => {
    if (isShowAll !== true) {
      const projectIdList =
        changeDetails?.map((item: any) => item?.project_id || '') || [];
      setExpandItemList(projectIdList);
    } else {
      setExpandItemList([]);
    }
    toggleIsShowAll();
  };

  const handleDownloadInPage = (fileType?: string) => {
    const fileTypeTmp = fileType || FILE_TYPES_MAP.CSV;
    const filterParams = formatFilterFetchParams(filter, filterConditions);
    const sortParams = formatSortFetchParams(sort);
    const searchParams = formatSearchParams(search);
    handleDownload({
      ...filterParams,
      ...sortParams,
      ...searchParams,
      fileType: fileTypeTmp,
      file_type: fileTypeTmp,
    });
  };

  useEffect(() => {
    if (initialLoading) return;
    updateChangeDetails(initialFilter as any, filterConditions);
    onUpdateShortUrlParams({ filter: initialFilter });
  }, [initialLoading]);

  usePageLog({ pageId: 'PIPELINE_CHANGE_LOG_CHANGE_DETAIL' });

  // 刷新搜索组件弹出面板位置的依赖数组
  const searchSelectUpdateDeps = useMemo<React.DependencyList>(() => [], []);

  useEffect(() => {
    const handleResize = () => {
      const hValue = window.innerHeight;

      if (filterHeightRef.current) {
        const filterRefHeight =
          filterHeightRef.current?.getBoundingClientRect().height;
        let varHeight = 0;
        if (filterRefHeight === 46) {
          varHeight = 46;
        } else if (filterRefHeight === 36) {
          varHeight = 50;
        } else {
          varHeight = 92;
        }
        const valueH = hValue - varHeight - 243 - 79.3;
        setTableHeight(valueH);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [initialLoading]);

  return (
    <div
      style={style}
      className={classnames(
        'dbg-pipeline-change-log-change-detail dbg-pipeline-table-card-body-bottom0px',
        className,
      )}
    >
      <Justify
        className="dbg-common-page__toolbar-justify"
        right={
          <div className="dbg-common-page__toolbar">
            <SearchCommon
              placeholder="Search any project in the list"
              showRightBtn={showRightBtn}
              currentPage={currentPage}
              updateDeps={searchSelectUpdateDeps}
              submit={handleSearchSubmit}
              getSearchList={(payload: any) => {
                return getSearchList(payload, {
                  sort,
                });
              }}
              formatSearchResults={formatSearchProjectsResults}
              searchVal={search?.search_keyword}
              searchList={search?.search_list}
              onRightBtnClick={(payload: any, isJump = true) => {
                const { project_id: id, name } = payload || {};
                const params = { id, name, tab: 'gameDetail' };
                return toProjectDetails(params, null, isJump);
              }}
              setSearchList={setSearchListHandle}
              setSearchVal={setSearchValHandle}
            />
            <MultiTypeDownloadBtn
              isDisabled={changeDetailsLoading || !changeDetails?.length}
              isLoading={isDownloading}
              options={SIMPLE_DOWNLOAD_FILE_TYPES1}
              onSelect={handleDownloadInPage}
            />
          </div>
        }
      />
      <ChangeDetailFilter
        loading={filterConditionsLoading}
        data={filterConditions}
        value={filter}
        onChange={handleFilterChange}
        allFilterOptionsValues={allValuesInterface}
        onReset={handleReset}
        headerTitle={headerTitle}
        scrollBody={scrollBody}
        currentPage={currentPage}
        updateDeps={searchSelectUpdateDeps}
        handleSearch={handleSearchSubmit}
        getSearchList={(payload: any) => {
          return getSearchList(payload, {
            sort,
          });
        }}
        formatSearchResults={formatSearchProjectsResults}
        searchVal={search?.search_keyword}
        searchList={search?.search_list}
        showRightBtn={showRightBtn}
        selectedValue={selectedValue}
        setSelectedValue={setSelectedValue}
        setSearchList={setSearchListHandle}
        setSearchVal={setSearchValHandle}
      />

      <div
        className="dbg-pipeline-change-log-change-detail__topbar"
        ref={filterHeightRef}
      >
        <ToggleAllItems
          isShowAll={isShowAll}
          onToggleIsShowAll={handleShowAll}
          isExpandAll={true}
          style={{ marginBottom: '10px' }}
        />
      </div>

      <Card className="dbg-tab-page__card dbg-pipeline-change-log-change-detail-card">
        <Card.Body className="dbg-tab-page__card-body">
          <ChangeDetailTable
            loading={changeDetailsLoading}
            error={changeDetailsError}
            isShowAll={isShowAll}
            data={changeDetails}
            sort={sort}
            onSortChange={handleSortChange}
            onRetry={handleRetry}
            onReset={handleReset}
            expandItemList={expandItemList}
            setExpandItemList={setExpandItemList}
            tableHeight={tableHeight}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default ChangeDetail;
